// 🛡 PlayLanguage Security (sidebar Безпека → 🛡 PlayLanguage)
// Standalone page — інциденти + заблоковані юзери з можливістю block/unblock.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Alert, Tooltip, Divider,
} from '@mui/material';

const API_BASE = '/api/dashboard/apps/playlanguage';

async function jsonFetch(url, opts = {}) {
  const r = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', ...opts,
  });
  if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
  return r.json();
}
const fmtDate = (s) => s ? new Date(s).toLocaleString() : '—';

export default function PlayLanguageSecurityPage() {
  const [incidents, setIncidents] = useState([]);
  const [blocked, setBlocked]   = useState([]);
  const [filter, setFilter]     = useState('open');
  const [busy, setBusy]         = useState(false);

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/security/incidents?status=${filter}`)
      .then(r => setIncidents(r.incidents || []));
    jsonFetch(`${API_BASE}/security/blocked`).then(r => setBlocked(r.blocked || []));
  }, [filter]);
  useEffect(load, [load]);

  const resolve = async (id) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/incidents/${id}/resolve`,
      { method: 'POST', body: '{}' }); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const blockPerm = async (uid) => {
    if (!window.confirm('Заблокувати назавжди?')) return;
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/block`,
      { method: 'POST', body: JSON.stringify({ user_id: uid, hours: null, reason: 'admin permanent' }) });
      load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const blockTemp = async (uid, hours) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/block`,
      { method: 'POST', body: JSON.stringify({ user_id: uid, hours, reason: `admin temp ${hours}h` }) });
      load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const unblock = async (uid) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/unblock/${uid}`, { method: 'POST', body: '{}' }); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 6 }}>
      <Typography variant="h4" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold', mb: 1 }}>
        🛡 PlayLanguage — Безпека
      </Typography>
      <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
        Інциденти при завантаженні квитанцій + ручний block/unblock
      </Typography>

      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ color: '#fff' }}>📋 Інциденти</Typography>
          <Box flex={1} />
          {['open', 'resolved', 'all'].map(s =>
            <Chip key={s} label={s} onClick={() => setFilter(s)}
              variant={filter === s ? 'filled' : 'outlined'}
              sx={{ bgcolor: filter === s ? 'var(--ac1, #7c4dff)' : 'transparent',
                    color: filter === s ? '#fff' : '#aaa', borderColor: '#555' }} />)}
        </Stack>
        {incidents.length === 0
          ? <Alert severity="info">Немає інцидентів зі статусом "{filter}"</Alert>
          : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['ID','Час','User','Тип','Деталі','IP','Block?','Actions'].map(c =>
                      <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incidents.map(i => (
                    <TableRow key={i.id} sx={{ '& td': { color: '#fff' } }}>
                      <TableCell>#{i.id}</TableCell>
                      <TableCell>{fmtDate(i.created_at)}</TableCell>
                      <TableCell>
                        {i.username ? `@${i.username}` : '—'}
                        <br />
                        <span style={{ color: '#888', fontSize: '0.75rem' }}>
                          tg:{i.telegram_id} db:{i.user_id || '?'}
                        </span>
                      </TableCell>
                      <TableCell><Chip size="small" label={i.type} color="warning" /></TableCell>
                      <TableCell>
                        <Tooltip title={JSON.stringify(i.details || {}, null, 2)}>
                          <Typography variant="caption" sx={{ color: '#fca5a5' }}>
                            {String(i.details?.reason || i.details?.filename || '').slice(0, 40)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: '#888' }}>{i.ip || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {i.is_blocked
                          ? <Chip size="small" label={i.blocked_until ? `до ${fmtDate(i.blocked_until)}` : 'назавжди'}
                                  color="error" />
                          : <span style={{ color: '#888' }}>—</span>}
                      </TableCell>
                      <TableCell align="right">
                        {i.status === 'open' &&
                          <Button size="small" disabled={busy} onClick={() => resolve(i.id)}
                                  sx={{ color: '#5eead4' }}>✓ Resolve</Button>}
                        {i.user_id && !i.is_blocked && (
                          <>
                            <Button size="small" disabled={busy} onClick={() => blockTemp(i.user_id, 1)}
                                    sx={{ color: '#fbbf24' }}>1h</Button>
                            <Button size="small" disabled={busy} onClick={() => blockTemp(i.user_id, 24)}
                                    sx={{ color: '#fb923c' }}>24h</Button>
                            <Button size="small" disabled={busy} onClick={() => blockPerm(i.user_id)}
                                    sx={{ color: '#ef4444' }}>∞</Button>
                          </>
                        )}
                        {i.user_id && i.is_blocked && (
                          <Button size="small" disabled={busy} onClick={() => unblock(i.user_id)}
                                  sx={{ color: '#5eead4' }}>🔓 Unblock</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        <Divider sx={{ borderColor: '#444' }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          🚫 Заблоковані юзери ({blocked.length})
        </Typography>
        {blocked.length === 0
          ? <Alert severity="success">Зараз нікого не заблоковано.</Alert>
          : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['User','Заблоковано','До','Причина','Actions'].map(c =>
                      <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blocked.map(b => (
                    <TableRow key={b.user_id} sx={{ '& td': { color: '#fff' } }}>
                      <TableCell>
                        {b.username ? `@${b.username}` : '—'}
                        <br />
                        <span style={{ color: '#888', fontSize: '0.75rem' }}>
                          tg:{b.telegram_id} db:{b.user_id}
                        </span>
                      </TableCell>
                      <TableCell>{fmtDate(b.blocked_at)}</TableCell>
                      <TableCell>
                        {b.permanent
                          ? <Chip size="small" label="назавжди" color="error" />
                          : fmtDate(b.blocked_until)}
                      </TableCell>
                      <TableCell><Typography variant="caption">{b.reason}</Typography></TableCell>
                      <TableCell align="right">
                        <Button size="small" disabled={busy} onClick={() => unblock(b.user_id)}
                                sx={{ color: '#5eead4' }}>🔓 Unblock</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Stack>
    </Container>
  );
}
